import { WhereClauseModalContent } from '@modules/common/components/WhereClauseField/WhereClauseModalContent';
import { WhereClausePreviewProps } from '@modules/common/components/WhereClausePreview/WhereClausePreview';
import { MeasuredAsMetricDenominatorPreview } from '@modules/quantify/components/QuantifyQueryBuilder/Fields/MeasuredAsField/MeasuredAsMetricDenominatorPreview';
import {
  useFetchQuantifyReport,
  useTriggerQuantifyQueryAnalyticsEvent,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { isValidQuery } from '@modules/quantify/utils';
import { QueryBuilderActionsHandle } from '@modules/queryBuilder/components/QueryBuilder/QueryBuilder';
import { QueryBuilderStoreProvider } from '@modules/queryBuilder/store/QueryBuilderStoreProvider';
import EditIcon from '@mui/icons-material/Edit';
import {
  Dialog,
  IconButton,
  Paper,
  Stack,
  SxProps,
  Theme,
  Typography,
  styled,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { mapToEQL } from 'v2/core/repository/eql/mappers';
import { EQLFunctionName, ExpressionType } from 'v2/core/repository/eql/types';
import {
  EmptyWhereClause,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';

interface MeasuredAsMetricDenominatorProps {
  sx?: SxProps<Theme>;
  isReadOnly?: boolean;
  WhereClausePreviewProps?: Partial<WhereClausePreviewProps>;
}

const StyledPaper = styled(Paper)`
  & {
    position: absolute;
    top: 50px;
  }
`;

export function MeasuredAsMetricDenominator({
  sx,
  isReadOnly,
  WhereClausePreviewProps,
}: MeasuredAsMetricDenominatorProps) {
  const blockEditAction = useQuantifyStore(({ query }) => !!query.compareTo);
  const isNormaliseByPercentFormula = useQuantifyStore(
    ({ query }) => query.formulaID === EQLFunctionName.NormaliseByPercent,
  );
  const denominatorWhereClause = useQuantifyStore(
    ({ query }) => query.denominatorWhereClause,
  );
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.measuredAsCriteria,
  );
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const [shouldOpenQBModal, setShouldOpenQBModal] = useState(false);
  const queryBuilderRef = useRef<QueryBuilderActionsHandle>(null);
  const isValidQueryState = useQuantifyStore(({ query }) =>
    isValidQuery(query),
  );
  const fetchReportData = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);
  const trackQuantifyQueryEvents = useTriggerQuantifyQueryAnalyticsEvent();
  const { data: sourceDetailsMap } = useGetSourceDetailsMap();

  useEffect(() => {
    if (shouldFetchReportData && isValidQueryState) {
      setShouldFetchReportData(false);
      fetchReportData();
      trackQuantifyQueryEvents('Quantify - Query Executed');
    }
  }, [
    fetchReportData,
    isValidQueryState,
    shouldFetchReportData,
    trackQuantifyQueryEvents,
  ]);

  const openWhereClauseModal = () => {
    setShouldOpenQBModal(true);
  };

  const closeWhereClauseModal = (
    _?: {},
    reason?: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (reason === 'backdropClick') {
      return;
    }

    setShouldOpenQBModal(false);
    queryBuilderRef.current?.resetQuery(denominatorWhereClause ?? undefined);
  };

  const onChange = useCallback(
    ({ whereClause }: { whereClause: WhereClause | null }) => {
      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: {
          denominatorWhereClause: whereClause,
        },
      });

      dispatch({
        type: QuantifyActionType.SET_METADATA,
        payload: {
          shouldShowTopDataPoints: true,
          visibleDataPoints: [],
        },
      });

      setShouldFetchReportData(true);
      try {
        trackQuantifyQueryEvents('Quantify - Denominator WhereClause Changed', {
          denominatorWhereClauseEQL: mapToEQL({
            whereClause: whereClause ?? EmptyWhereClause,
            expressionType: ExpressionType.Records,
            sourceDetailsMap: sourceDetailsMap ?? new Map(),
          }),
        });
      } catch {}
    },
    [dispatch, trackQuantifyQueryEvents, sourceDetailsMap],
  );

  const editIcon =
    isReadOnly || blockEditAction ? null : (
      <IconButton
        size="small"
        onClick={openWhereClauseModal}
        aria-label="open-denominator-qb-modal-btn"
      >
        <EditIcon fontSize="inherit" />
      </IconButton>
    );

  if (!isNormaliseByPercentFormula) {
    return null;
  }

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={sx}
      data-testid="denominator-where-clause-field"
    >
      <Stack direction="row" alignItems="center">
        <Typography variant="caption">
          <Typography variant="inherit" component="span">
            Denominator is{' '}
          </Typography>
          <Typography variant="inherit" component="span" color="text.disabled">
            All {measuredAsCriteria?.displayName} with following Filters
          </Typography>
        </Typography>
        {editIcon}
      </Stack>
      <MeasuredAsMetricDenominatorPreview
        isReadOnly={isReadOnly || blockEditAction}
        WhereClausePreviewProps={WhereClausePreviewProps}
        onClick={openWhereClauseModal}
      />
      {!isReadOnly && (
        <QueryBuilderStoreProvider>
          <Dialog
            data-testid="denominator-where-clause-modal"
            open={shouldOpenQBModal}
            fullWidth={true}
            maxWidth="md"
            PaperProps={{
              elevation: 3,
            }}
            keepMounted={true}
            onClose={closeWhereClauseModal}
            PaperComponent={StyledPaper}
          >
            <WhereClauseModalContent
              ref={queryBuilderRef}
              whereClause={denominatorWhereClause ?? undefined}
              onChange={onChange}
              onClose={closeWhereClauseModal}
              shouldSkipRender={!shouldOpenQBModal}
              modalTitle={''}
              forceEnableExecuteButton={true}
            />
          </Dialog>
        </QueryBuilderStoreProvider>
      )}
    </Stack>
  );
}
